import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { QcResponseGrid } from 'src/app/shared/models/grid.model';
import { IS_ACTION, IS_LOAD, IS_SEARCH } from 'src/app/shared/models/load.models';
import { QcMerchantResponse, QcResponse } from 'src/app/shared/models/qcResponse.model';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { GridFacadeService } from 'src/app/shared/services/grid.facade.service';
import { FlowsLiteralsFacadeService } from 'src/app/shared/services/literals/flows.literals.facade.service';
import { PaginatorLiteralsFacadeService } from 'src/app/shared/services/literals/paginator.literals.facade.service';
import { PaginatorFacadeService } from 'src/app/shared/services/paginator.facade.service';
import { Flow, FlowValidateState, SearchFlowsPayload } from '../models/flows.model';
import { EditFlowFacadeService } from './edit-flow.facade.service';
import { FlowConfigurationFacadeService } from './flow-configuration.facade.service';
import { SearchFlowsFacadeService } from './search-flows.facade.service';

@Injectable({ providedIn: 'root' })
export class SearchFlowsService {
  constructor(
    private http: HttpClient,
    private environmentFacadeService: EnvironmentFacadeService,
    private gridFacade: GridFacadeService,
    private paginatorFacadeService: PaginatorFacadeService,
    private searchFlowsFacadeService: SearchFlowsFacadeService,
    private flowsLiterals: FlowsLiteralsFacadeService,
    private paginatorLiterals: PaginatorLiteralsFacadeService,
    private flowConfigurationFacade: FlowConfigurationFacadeService,
    private editFlowFacade: EditFlowFacadeService,
  ) {}

  getFlows(): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/';

    return this.http.get<QcResponseGrid>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  getFlowsTemplates(): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flowtemplate';

    return this.http.get<QcResponseGrid>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  searchFlows(): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/search';

    const body: SearchFlowsPayload = {
      criteriaList: [
        {
          criteriaType: 'FLOW_CONTEXT',
          flowContext: this.searchFlowsFacadeService.flowContext ? this.searchFlowsFacadeService.flowContext : null,
        },
      ],
      pagination: {
        page: this.paginatorFacadeService.pageIndex + 1,
        pageSize: this.paginatorFacadeService.itemsNumber,
      },
      sorting: {
        sortingField:
          this.paginatorFacadeService.selectedSortField === this.flowsLiterals.sortCreationDate
            ? 'FLOW_CREATION_DATE'
            : this.paginatorFacadeService.selectedSortField === this.flowsLiterals.sortContextFlow
              ? 'FLOW_CONTEXT'
              : 'FLOW_ID',
        sortingOrder: this.paginatorFacadeService.selectedOrder === this.paginatorLiterals.orderAsc ? 'ASC' : 'DESC',
      },
    };

    if (this.searchFlowsFacadeService.flowName)
      body.criteriaList.push({ criteriaType: 'FLOW_ID', flowId: this.searchFlowsFacadeService.flowName });
    if (this.searchFlowsFacadeService.activeFlows)
      body.criteriaList.push({ criteriaType: 'ACTIVE_FLOW', deleteFlag: false });
    if (this.searchFlowsFacadeService.deactivatedFlows)
      body.criteriaList.push({
        criteriaType: 'INACTIVE_FLOW',
        deleteFlag: true,
      });

    return this.http.post<QcResponseGrid>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  deactivateFlow(row?: any): Observable<QcMerchantResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/update';

    const body = row
      ? {
          flowId: row.flowID,
          deleteFlag: row.deleteFlag ? false : true,
          flowContext: row.flowContext,
          flowName: row.flowName,
          taskFlow: row.flowTask,
        }
      : {
          flowId: this.gridFacade.selectedRow.flowID,
          deleteFlag: this.gridFacade.selectedRow.deleteFlag ? false : true,
          flowContext: this.gridFacade.selectedRow.flowContext,
          flowName: this.gridFacade.selectedRow.flowName,
          taskFlow: this.gridFacade.selectedRow.flowTask,
        };

    return this.http.put<QcMerchantResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  getFlowEvents(): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/context/with/events';

    return this.http.get<QcResponseGrid>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  getFlowContext(contextFlow: string): Observable<QcResponseGrid> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flowevent/context/' + contextFlow;

    return this.http.get<QcResponseGrid>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  getAddFlows(): Observable<Flow[]> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/' + this.flowConfigurationFacade.contextFlow;

    return this.http.get<Flow[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  getTableFlows(flowEvent: string): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flowevent/' + flowEvent;

    return this.http.get<QcResponse>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_SEARCH, true),
    });
  }

  updateConfiguration(): Observable<QcResponse> {
    const url =
      this.environmentFacadeService.getEnvironment() + '/rest/v1/flowevent/' + this.flowConfigurationFacade.flowEvent;

    const body = this.flowConfigurationFacade.flowTable;

    return this.http.put<QcResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  validateJSON(): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/validate';

    const body: FlowValidateState = {
      deleteFlag: !this.editFlowFacade.activeFlows,
      flowContext: this.editFlowFacade.flowContext,
      flowId: this.editFlowFacade.flowName,
      flowName: this.editFlowFacade.flowName,
      taskFlow: this.editFlowFacade.jsonData,
    };

    return this.http.post<QcResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  createJSON(): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/create';

    const body: FlowValidateState = {
      deleteFlag: !this.editFlowFacade.activeFlows,
      flowContext: this.editFlowFacade.flowContext,
      flowId: this.editFlowFacade.flowName,
      flowName: this.editFlowFacade.flowName,
      taskFlow: JSON.stringify(JSON.parse(this.editFlowFacade.jsonData)),
    };

    return this.http.post<QcResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  saveJSON(): Observable<QcResponse> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/flows/update';

    const body: FlowValidateState = {
      deleteFlag: !this.editFlowFacade.activeFlows,
      flowContext: this.editFlowFacade.flowContext,
      flowId: this.editFlowFacade.flowName,
      flowName: this.editFlowFacade.flowName,
      taskFlow: JSON.stringify(JSON.parse(this.editFlowFacade.jsonData)),
    };

    return this.http.put<QcResponse>(url, body, {
      withCredentials: true,
      context: new HttpContext().set(IS_ACTION, true),
    });
  }

  getLanguages(): Observable<any[]> {
    const url = this.environmentFacadeService.getEnvironment() + '/rest/v1/locale/findAvailableLanguages';

    return this.http.get<any[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }
}
